import { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigation } from '@react-navigation/native';
import i18n from 'i18n-js';

import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { deleteUser, getById, updateUserProfile, changeAccess, inviteUser } from '@reducers/membership';
import { getDeviceByOwner } from '@reducers/device';
import { navigateBack, Alert, ROLES, ROLE_ITEMS, localTime, getStateParams, getSecureValue } from '@helpers';
import { scale, isAdmin, isManager } from '@components/frame';
import { buildSettingsNavParams, type SettingsRootStack } from '@components/settings/v2/common/navigationParams';
import type { RootState } from '@store/types';

export interface UseManageUserDetailsParams {
  route: { params: { id: string } };
  rootStack?: SettingsRootStack;
}

export const useManageUserDetails = ({ route, rootStack = 'Settings' }: UseManageUserDetailsParams) => {
  const dispatch = useAppDispatch();
  const navigation = useNavigation();

  const users = useSelector((state: RootState) => state.membership?.users ?? []);
  const userId = getStateParams(route).id;

  const currentUser = users.find((d: any) => d.userProfile?.userId === userId);

  const [userData, setUser] = useState<any>(currentUser || {});
  const [showEditUserName, setShowEditUserName] = useState(false);
  const [showEditRole, setShowEditRole] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [changingAccess, setChangingAccess] = useState(false);
  const [devices, setDevices] = useState<any[]>([]);

  const isSelf = Boolean(userData.emailAddress) && userData.emailAddress === getSecureValue('email');
  const isRegistered = userData.status === 'Registered';
  const showActions = isAdmin || (isManager && userData.role === ROLES.OPERATOR);

  const toggleEditUserName = useCallback(() => {
    setShowEditUserName((prev) => !prev);
  }, []);

  const getDevices = useCallback(async (email: string) => {
    const d = await dispatch(getDeviceByOwner(email));
    setDevices((d as any)?.devices || []);
  }, [dispatch]);

  useEffect(() => {
    if (!userData.userProfile?.userId) {
      (async () => {
        const d = await dispatch(getById(userId));
        if ((d as any)?.users?.[0]) {
          setUser((d as any).users[0]);
          getDevices((d as any).users[0].emailAddress);
        } else {
          setUser({});
        }
      })();
    } else {
      getDevices(userData.emailAddress);
    }
  }, []);

  const triggerBlueButton = useCallback(async () => {
    if (changingAccess) {
      return;
    }
    setChangingAccess(true);
    if (isRegistered) {
      await dispatch(
        changeAccess({
          identifierType: 'User',
          identifier: userData.emailAddress,
          blocked: !userData.isBlocked,
        }),
      );
      setUser({ ...userData, isBlocked: !userData.isBlocked });
    } else {
      await dispatch(
        inviteUser({
          emailAddress: userData.emailAddress,
          role: userData.role,
          userProfile: {
            firstName: userData.userProfile.firstName,
            lastName: userData.userProfile.lastName,
          },
        }),
      );
    }
    setChangingAccess(false);
  }, [changingAccess, isRegistered, userData, dispatch]);

  const deleteUserFN = useCallback(async () => {
    setDeleting(true);
    const result: any = await dispatch(deleteUser(userData.emailAddress));
    if (result && result.isSuccess) {
      navigateBack();
    }
    setDeleting(false);
  }, [userData, dispatch]);

  const triggerDelete = useCallback(() => {
    Alert({
      title: i18n.t('Are you sure?'),
      actions: [
        {
          text: i18n.t('No'),
          onPress: () => {},
          style: 'primary',
        },
        {
          text: i18n.t('Yes'),
          onPress: async () => {
            await deleteUserFN();
          },
        },
      ],
    });
  }, [deleteUserFN]);

  const { userProfile = {} } = userData;

  const userFields: Record<string, any> = {
    name: {
      value: `${userProfile.firstName} ${userProfile.lastName}`,
      name: i18n.t('Name'),
      edit: isAdmin,
      noArrow: !isAdmin,
      onClick: isAdmin ? toggleEditUserName : () => {},
    },
    emailAddress: {
      value: userData.emailAddress,
      name: i18n.t('Email'),
      noArrow: true,
      defaultValue: true,
    },
    role: {
      value: i18n.t(userData.role),
      name: i18n.t('Role'),
      edit: isAdmin && !isSelf,
      noArrow: !isAdmin || isSelf,
      onClick: isAdmin && !isSelf ? () => setShowEditRole(true) : () => {},
    },
  };

  if (isRegistered) {
    userFields.lastUsedOn = {
      value: userData.lastUsedOn ? localTime(userData.lastUsedOn).format('DD MMM YYYY [at] HH:mm') : '',
      name: i18n.t('Last used'),
      noArrow: true,
    };
    userFields.location = {
      value: userData.location,
      name: i18n.t('Last location'),
      noArrow: true,
    };
    userFields.ip = {
      value: userData.ip,
      name: i18n.t('Last IP'),
      noArrow: true,
    };
    userFields.registeredOn = {
      value: userData.registeredOn ? localTime(userData.registeredOn).format('DD MMM YYYY [at] HH:mm') : '',
      name: i18n.t('Registered on'),
      noArrow: true,
    };
  }

  if (userData.invitedBy && userData.invitedOn) {
    userFields.invitedBy = {
      value: userData.invitedBy,
      name: i18n.t('Invited by'),
      noArrow: true,
      defaultValue: true,
    };
  }

  const editNameConfig = {
    header: 'Name',
    subHeader: `${i18n.t('Edit the name of')} ${userProfile.firstName} ${userProfile.lastName}`,
    initData: {
      firstName: userProfile.firstName,
      lastName: userProfile.lastName,
    },
    onSubmit: async (data: any, onClose: () => void) => {
      await dispatch(
        updateUserProfile(
          {
            emailAddress: userData.emailAddress,
            userProfile: data,
          },
          () => {
            setUser({
              ...userData,
              userProfile: data,
            });
            onClose();
          },
        ),
      );
    },
    inputs: [
      {
        key: 'firstName',
        name: 'First name',
        noArrow: true,
        lableWidth: scale(110),
        placeholder: 'First Name',
      },
      {
        key: 'lastName',
        name: 'Last name',
        placeholder: 'Last Name',
        noArrow: true,
        lableWidth: scale(110),
      },
    ],
  };

  const updateUserRole = useCallback(
    async (role: any) => {
      await dispatch(
        updateUserProfile({ emailAddress: userData.emailAddress, role: role.value }, () => {
          setUser({ ...userData, role: role.value });
          setShowEditRole(false);
        }),
      );
    },
    [userData, dispatch],
  );

  const navigateToDeviceDetails = useCallback(
    (deviceId: string) => {
      navigation.navigate(rootStack as never, buildSettingsNavParams(rootStack, 'Devices', 'DeviceDetails', { id: deviceId }) as never);
    },
    [navigation, rootStack],
  );

  return {
    userData,
    userFields,
    userProfile,
    devices,
    isSelf,
    isRegistered,
    showActions,
    showEditUserName,
    toggleEditUserName,
    showEditRole,
    setShowEditRole,
    deleting,
    changingAccess,
    triggerBlueButton,
    triggerDelete,
    editNameConfig,
    roleItems: ROLE_ITEMS,
    updateUserRole,
    navigateToDeviceDetails,
  };
};
